import React, { useCallback, useEffect, useState } from 'react';
import { Globe, Loader2, Mail, Phone, PhoneForwarded, PhoneMissed, Store, Trash2 } from 'lucide-react';
import { salesAPI } from '../../services/salesApi';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { confirmDiscard } from '../../utils/unsavedChanges';
import { SheetField, SheetSection } from '../settings/sheet';
import {
  ASIDE_BTN, DANGER_GHOST_BTN, DetailPanel, Disclosure, GHOST_BTN, INPUT_CLS, Notice, PRIMARY_BTN, SELECT_CLS, StatusChip,
} from '../shared/listPage';
import {
  ACTIVITY_TYPE_OPTIONS, BUSINESS_STAGE_OPTIONS, KIND_OPTIONS, LOST_REASON_OPTIONS, PLATFORM_OPTIONS, SIGNAL_OPTIONS,
  SOURCE_OPTIONS, STATUS_OPTIONS, TRACK_OPTIONS, Lead, LeadActivity, LeadInput, QuickAction, SalesMeta,
  activityTitle, findDuplicates, kindLabel, quickActionPayload, sourceLabel, stageProblem, statusLabel, statusTone,
} from './salesModel';

/**
 * Κάρτα υποψηφίου (Πωλήσεις). Με τη σειρά που δουλεύει κανείς ένα τηλεφώνημα:
 *  1. «Πριν την κλήση» — γιατί τώρα και η πρώτη φράση (από την έρευνα), μόνο αν υπάρχουν·
 *  2. «Επόμενο βήμα» — στάδιο, τι και πότε, υπεύθυνος·
 *  3. «Καταγραφή επαφής» — «Δεν απάντησε» / «Ξαναπάρε» με ένα πάτημα, ή κλήση/επίσκεψη/σημείωση με κείμενο.
 *     Ό,τι άλλαξε στη φόρμα φεύγει ΜΑΖΙ, σε μία συναλλαγή·
 *  4. «Ιστορικό»·
 *  5. «Στοιχεία» — τα βασικά ανοιχτά, τα υπόλοιπα στο «Περισσότερα στοιχεία».
 * Οι πηγές της έρευνας μένουν στη βάση αλλά δεν φαίνονται (απόφαση χρήστη 3/11/2026: «περιττές πληροφορίες»), όπως
 * και ο υπεύθυνος (ένα άτομο κάνει όλες τις πωλήσεις). Ίδιο τηλέφωνο/email/επωνυμία με άλλον υποψήφιο → προειδοποίηση
 * με σύνδεσμο προς εκείνον (δεν εμποδίζει την αποθήκευση: δύο καταστήματα αλυσίδας μπορεί να έχουν ίδιο τηλέφωνο).
 * `leadId` null = νέος υποψήφιος: μόνο στοιχεία + στάδιο.
 */

const EMPTY: LeadInput = {
  name: '', kind: 'other', track: 'single', business_stage: null, area: null, address: null, stores_count: null,
  stores_note: null, platforms: [], phone: null, phone_note: null, email: null, website: null, contact_name: null,
  current_system: null, contract_end: null, signals: [], why_now: null, opener: null, fit_score: null, source: 'other',
  referred_by_store_id: null, referred_by_note: null, status: 'new', owner_id: null, next_action: null,
  next_action_at: null, lost_reason: null, lost_note: null, retry_at: null, won_store_id: null, notes: null,
  expected_monthly: null,
};
const FORM_KEYS = Object.keys(EMPTY) as Array<keyof LeadInput>;
const toInput = (l: Lead): LeadInput => Object.fromEntries(FORM_KEYS.map(k => [k, l[k as keyof Lead] ?? EMPTY[k]])) as LeadInput;

const errorText = (e: any, fallback: string) => e?.response?.data?.error || fallback;
const dtfmt = (iso: string) => new Date(iso).toLocaleString('el-GR', {
  timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
const EMPTY_CONTACT = { type: 'call', outcome: '', body: '' };

interface Props {
  open: boolean;
  leadId: number | null;
  meta: SalesMeta | null;
  /** Όλοι οι υποψήφιοι — για την προειδοποίηση διπλών. */
  allLeads: Lead[];
  isSuper: boolean;
  today: string;
  onClose: () => void;
  onSaved: (lead: Lead) => void;
  /** Άνοιγμα άλλου υποψηφίου (από την προειδοποίηση διπλών). */
  onOpenLead: (id: number) => void;
  onDeleted: (id: number) => void;
  /** «Πελάτης» χωρίς κατάστημα → «Νέο κατάστημα» συμπληρωμένο (μόνο ο super admin το περνά). */
  onCreateStore?: (lead: Lead) => void;
}

const LeadPanel: React.FC<Props> = ({
  open, leadId, meta, allLeads, isSuper, today, onClose, onSaved, onOpenLead, onDeleted, onCreateStore,
}) => {
  const isNew = leadId === null;
  const form = useDirtyForm<LeadInput>(EMPTY, { guardId: 'sales-lead' });
  const { values: v, setField, reset } = form;
  const [lead, setLead] = useState<Lead | null>(null);
  const [activities, setActivities] = useState<LeadActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contact, setContact] = useState(EMPTY_CONTACT);
  const [moreOpen, setMoreOpen] = useState(false);

  const load = useCallback(async (id: number) => {
    const d = await salesAPI.getLead(id);
    setLead(d.lead);
    setActivities(d.activities || []);
    reset(toInput(d.lead));
    return d.lead;
  }, [reset]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setContact(EMPTY_CONTACT);
    setMoreOpen(false);
    if (leadId === null) {
      setLead(null);
      setActivities([]);
      reset({ ...EMPTY, owner_id: meta?.owners.some(o => o.id === meta.me) ? meta.me : null });
      return;
    }
    setLoading(true);
    load(leadId).catch(e => setError(errorText(e, 'Ο υποψήφιος δεν φόρτωσε'))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId]);

  const close = () => {
    if (form.dirty && !confirmDiscard()) return;
    reset();
    onClose();
  };

  const run = async (action: () => Promise<Lead>) => {
    setBusy(true);
    setError(null);
    try {
      const saved = await action();
      onSaved(saved);
      return saved;
    } catch (e) {
      setError(errorText(e, 'Η αποθήκευση απέτυχε'));
      return null;
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    const problem = stageProblem(v);
    if (problem) { setError(problem); return; }
    if (isNew) {
      // Ο γονέας ανοίγει την κάρτα του νέου (onSaved) — αμέσως μετά έρχεται συνήθως η πρώτη επαφή.
      const created = await run(async () => (await salesAPI.createLead(v)).lead);
      if (created) reset(toInput(created));
      return;
    }
    await run(async () => {
      await salesAPI.updateLead(leadId as number, form.changes);
      return load(leadId as number);
    });
  };

  // Γρήγορα κουμπιά: κλήση στο ιστορικό + επόμενο βήμα (μαζί με ό,τι άλλο άλλαξε στη φόρμα).
  const quick = async (action: QuickAction) => {
    if (!lead) return;
    const p = quickActionPayload(lead, action, today);
    await run(async () => {
      await salesAPI.addActivity(lead.id, p.activity, { ...form.changes, ...p.lead });
      return load(lead.id);
    });
  };

  const logContact = async () => {
    if (!lead) return;
    if (contact.type === 'note' && !contact.body.trim()) { setError('Η σημείωση είναι κενή'); return; }
    const problem = stageProblem(v);
    if (problem) { setError(problem); return; }
    const done = await run(async () => {
      await salesAPI.addActivity(lead.id, {
        type: contact.type, outcome: contact.type === 'call' && contact.outcome ? contact.outcome : null, body: contact.body.trim() || null,
      }, form.changes);
      return load(lead.id);
    });
    if (done) setContact({ ...EMPTY_CONTACT, type: contact.type });
  };

  const remove = async () => {
    if (!lead) return;
    if (!window.confirm(`Οριστική διαγραφή του «${lead.name}» μαζί με όλο το ιστορικό του;\n\nΜόνο για αίτημα διαγραφής στοιχείων — αλλιώς βάλε «Χάθηκε».`)) return;
    setBusy(true);
    try {
      await salesAPI.deleteLead(lead.id);
      reset();
      onDeleted(lead.id);
    } catch (e) {
      setError(errorText(e, 'Η διαγραφή απέτυχε'));
    } finally {
      setBusy(false);
    }
  };

  // ── Πεδία ────────────────────────────────────────────────────────────────
  const set = <K extends keyof LeadInput>(k: K) => (value: LeadInput[K]) => setField(k, value);
  const textField = (k: keyof LeadInput, label: string, opts: { type?: string; placeholder?: string; area?: boolean } = {}) => (
    <SheetField label={label} htmlFor={`sl-${k}`}>
      {opts.area ? (
        <textarea id={`sl-${k}`} rows={3} value={(v[k] as string | null) ?? ''} placeholder={opts.placeholder}
          onChange={e => setField(k, (e.target.value || null) as never)} className={INPUT_CLS} />
      ) : (
        <input id={`sl-${k}`} type={opts.type || 'text'} value={(v[k] as string | null) ?? ''} placeholder={opts.placeholder}
          onChange={e => setField(k, (e.target.value || null) as never)} className={INPUT_CLS} />
      )}
    </SheetField>
  );
  const selectField = (k: keyof LeadInput, label: string, options: Array<{ value: string; label: string }>, empty?: string) => (
    <SheetField label={label} htmlFor={`sl-${k}`}>
      <select id={`sl-${k}`} value={(v[k] as string | null) ?? ''} className={SELECT_CLS}
        onChange={e => setField(k, (e.target.value || null) as never)}>
        {empty !== undefined && <option value="">{empty}</option>}
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </SheetField>
  );
  const idSelect = (k: 'owner_id' | 'referred_by_store_id' | 'won_store_id', label: string,
    options: Array<{ id: number; name: string }>, empty: string) => (
    <SheetField label={label} htmlFor={`sl-${k}`}>
      <select id={`sl-${k}`} value={v[k] ?? ''} className={SELECT_CLS}
        onChange={e => set(k)(e.target.value ? Number(e.target.value) : null)}>
        <option value="">{empty}</option>
        {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </SheetField>
  );
  const checks = (k: 'platforms' | 'signals', label: string, options: Array<{ value: string; label: string }>) => (
    <fieldset className="space-y-2">
      <legend className="xs-label">{label}</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {options.map(o => {
          const list = v[k] || [];
          return (
            <label key={o.value} className="inline-flex items-center gap-2 text-sm text-t1 min-h-[32px]">
              <input type="checkbox" checked={list.includes(o.value)}
                onChange={e => set(k)(e.target.checked ? [...list, o.value] : list.filter(x => x !== o.value))} />
              {o.label}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
  const numberField = (k: 'stores_count' | 'fit_score', label: string, max: number) => (
    <SheetField label={label} htmlFor={`sl-${k}`}>
      <input id={`sl-${k}`} type="number" inputMode="numeric" min={k === 'fit_score' ? 1 : 0} max={max} value={v[k] ?? ''}
        onChange={e => set(k)(e.target.value === '' ? null : Math.trunc(Number(e.target.value)))} className={INPUT_CLS} />
    </SheetField>
  );

  const stores = meta?.stores || [];

  // Διπλοί: στον νέο πάντα, σε υπάρχοντα μόνο όταν αλλάζει επωνυμία/τηλέφωνο/email (αλλιώς θα φώναζε κάθε φορά).
  const identityChanged = form.changedKeys.some(k => k === 'name' || k === 'phone' || k === 'email');
  const duplicates = (isNew || identityChanged) ? findDuplicates(v, allLeads, lead?.id) : [];
  const openOther = (id: number) => {
    if (form.dirty && !confirmDiscard()) return;
    reset();
    onOpenLead(id);
  };
  const duplicateNotice = duplicates.length > 0 && (
    <Notice testId="sales-duplicates">
      <span>
        Μοιάζει με υποψήφιο που υπάρχει ήδη:{' '}
        {duplicates.map((d, i) => (
          <React.Fragment key={d.lead.id}>
            {i > 0 && ', '}
            <button type="button" className="font-semibold underline" onClick={() => openOther(d.lead.id)}>{d.lead.name}</button>
            {' '}({statusLabel(d.lead.status)} · {d.reasons.join(', ')})
          </React.Fragment>
        ))}
      </span>
    </Notice>
  );

  // ── Ενότητες ─────────────────────────────────────────────────────────────
  const briefing = lead && (lead.why_now || lead.opener) && (
    <div className="rounded-xl bg-surface-2 border border-line px-4 py-3 space-y-2 text-sm" data-testid="sales-briefing">
      <p className="xs-label">Πριν την κλήση</p>
      {lead.why_now && <p className="text-t2"><span className="font-medium text-t1">Γιατί τώρα: </span>{lead.why_now}</p>}
      {lead.opener && <p className="text-t2"><span className="font-medium text-t1">Πρώτη φράση: </span>«{lead.opener}»</p>}
    </div>
  );

  const nextStep = (
    <SheetSection id="sales-next" title="Επόμενο βήμα">
      <div className="xs-grid2">
        {selectField('status', 'Στάδιο', STATUS_OPTIONS)}
        {v.status === 'lost' && (
          <>
            {selectField('lost_reason', 'Γιατί χάθηκε', LOST_REASON_OPTIONS, '—')}
            {textField('retry_at', 'Ξαναδοκίμασε στις', { type: 'date' })}
          </>
        )}
        {v.status === 'later' && textField('retry_at', 'Ξαναδοκίμασε στις', { type: 'date' })}
        {v.status === 'won' && idSelect('won_store_id', 'Ποιο κατάστημα έγινε', stores, '—')}
        {v.status !== 'lost' && v.status !== 'later' && v.status !== 'won' && textField('next_action_at', 'Πότε', { type: 'date' })}
      </div>
      {v.status !== 'lost' && v.status !== 'later' && v.status !== 'won' && (
        <div className="mt-4">{textField('next_action', 'Τι', { placeholder: 'π.χ. Τηλέφωνο στον ιδιοκτήτη' })}</div>
      )}
      {v.status === 'lost' && (
        <div className="mt-4">{textField('lost_note', 'Λεπτομέρειες', { area: true, placeholder: 'π.χ. ποια λειτουργία έλειπε, ποιον ανταγωνιστή διάλεξε' })}</div>
      )}
      {/* «Έγινε πελάτης»: χωρίς κατάστημα ακόμη → η φόρμα «Νέο κατάστημα» συμπληρωμένη (super admin). Ο υποψήφιος
          γίνεται «Πελάτης» και δένεται με το κατάστημα ΜΕΤΑ τη δημιουργία, όχι πριν. */}
      {lead && v.status === 'won' && !v.won_store_id && (
        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="sales-won-store">
          {onCreateStore ? (
            <button type="button" className={PRIMARY_BTN} onClick={() => {
              if (form.dirty && !confirmDiscard()) return;
              reset();
              onCreateStore(lead);
            }}>
              <Store className="w-4 h-4" aria-hidden="true" /> Άνοιγμα νέου καταστήματος
            </button>
          ) : (
            <p className="text-sm text-t3">Το κατάστημα το ανοίγει ο super admin· μετά διάλεξέ το εδώ.</p>
          )}
        </div>
      )}
    </SheetSection>
  );

  const contactSection = lead && (
    <SheetSection id="sales-contact" title="Καταγραφή επαφής">
      <div className="flex flex-wrap gap-2 mb-4">
        <button type="button" className={GHOST_BTN} disabled={busy} onClick={() => quick('no_answer')} data-testid="sales-quick-no-answer">
          <PhoneMissed className="w-4 h-4" aria-hidden="true" /> Δεν απάντησε · αύριο
        </button>
        <button type="button" className={GHOST_BTN} disabled={busy} onClick={() => quick('callback')} data-testid="sales-quick-callback">
          <PhoneForwarded className="w-4 h-4" aria-hidden="true" /> Ξαναπάρε · σε 3 ημ.
        </button>
      </div>
      <div className="xs-grid2">
        <SheetField label="Είδος" htmlFor="sl-contact-type">
          <select id="sl-contact-type" className={SELECT_CLS} value={contact.type}
            onChange={e => setContact({ ...contact, type: e.target.value })}>
            {ACTIVITY_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </SheetField>
        {contact.type === 'call' && (
          <SheetField label="Αποτέλεσμα" htmlFor="sl-contact-outcome">
            <select id="sl-contact-outcome" className={SELECT_CLS} value={contact.outcome}
              onChange={e => setContact({ ...contact, outcome: e.target.value })}>
              <option value="">—</option>
              <option value="talked">Μιλήσαμε</option>
              <option value="callback">Να ξαναπάρουμε</option>
              <option value="meeting_set">Κλείσαμε ραντεβού</option>
              <option value="no_answer">Δεν απάντησε</option>
            </select>
          </SheetField>
        )}
      </div>
      <div className="mt-4">
        <SheetField label="Τι ειπώθηκε" htmlFor="sl-contact-body">
          <textarea id="sl-contact-body" rows={2} className={INPUT_CLS} value={contact.body}
            onChange={e => setContact({ ...contact, body: e.target.value })} />
        </SheetField>
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <p className="text-xs text-t3">{form.dirty ? 'Οι αλλαγές πιο πάνω αποθηκεύονται μαζί.' : ''}</p>
        <button type="button" className={PRIMARY_BTN} disabled={busy} onClick={logContact} data-testid="sales-log-contact">
          Καταγραφή
        </button>
      </div>
    </SheetSection>
  );

  const history = lead && (
    <SheetSection id="sales-history" title="Ιστορικό">
      {activities.length === 0 ? <p className="text-sm text-t3">Τίποτα ακόμη.</p> : (
        <ol className="divide-y divide-line" data-testid="sales-history">
          {activities.map(a => (
            <li key={a.id} className="py-2.5 first:pt-0">
              <p className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span className="font-medium text-t1">{activityTitle(a)}</span>
                <span className="text-xs text-t3 tabular-nums">{dtfmt(a.occurred_at)}{a.author_name ? ` · ${a.author_name}` : ''}</span>
              </p>
              {a.body && <p className="mt-0.5 text-sm text-t2 whitespace-pre-wrap">{a.body}</p>}
            </li>
          ))}
        </ol>
      )}
    </SheetSection>
  );

  const details = (
    <SheetSection id="sales-details" title="Στοιχεία">
      <div className="xs-grid2">
        {textField('name', 'Επωνυμία')}
        {selectField('kind', 'Είδος', KIND_OPTIONS)}
        {textField('contact_name', 'Συνομιλητής')}
        {textField('area', 'Περιοχή')}
        {textField('phone', 'Τηλέφωνο', { type: 'tel' })}
        {textField('email', 'Email', { type: 'email' })}
        {selectField('source', 'Πηγή', SOURCE_OPTIONS)}
        {/* Εκτίμηση του χρήστη για την «αξία pipeline» (migration 287) — όχι τιμή, δεν πάει στις Χρεώσεις. */}
        <SheetField label="Αξία (€/μήνα, εκτίμηση)" htmlFor="sl-expected_monthly">
          <input id="sl-expected_monthly" inputMode="decimal" placeholder="π.χ. 59" className={INPUT_CLS}
            value={v.expected_monthly === null || v.expected_monthly === undefined ? '' : String(v.expected_monthly)}
            onChange={e => setField('expected_monthly', e.target.value.replace(/[^\d.,]/g, '') || null)} />
        </SheetField>
        {v.source === 'referral' && idSelect('referred_by_store_id', 'Σύσταση από', stores, '—')}
      </div>
      <div className="mt-4">
        <Disclosure label="Περισσότερα στοιχεία" open={moreOpen} onToggle={setMoreOpen} testId="sales-more">
          <div className="space-y-4">
            <div className="xs-grid2">
              {selectField('track', 'Μέγεθος', TRACK_OPTIONS)}
              {numberField('stores_count', 'Καταστήματα', 100000)}
              {selectField('business_stage', 'Κατάσταση', BUSINESS_STAGE_OPTIONS, '—')}
              {numberField('fit_score', 'Βαθμός (1–5)', 5)}
              {textField('address', 'Διεύθυνση')}
              {textField('website', 'Site', { placeholder: 'https://…' })}
              {textField('current_system', 'Σύστημα ταμείου σήμερα')}
              {textField('contract_end', 'Λήξη συμβολαίου του', { type: 'date' })}
              {v.source === 'referral' && textField('referred_by_note', 'Σύσταση: σημείωση')}
            </div>
            {checks('platforms', 'Πλατφόρμες', PLATFORM_OPTIONS)}
            {checks('signals', 'Λόγοι να μας ακούσει τώρα', SIGNAL_OPTIONS)}
            {textField('why_now', 'Γιατί τώρα', { area: true })}
            {textField('opener', 'Πρώτη φράση της κλήσης', { area: true })}
            {textField('notes', 'Σημειώσεις', { area: true })}
          </div>
        </Disclosure>
      </div>
    </SheetSection>
  );

  return (
    <DetailPanel
      open={open}
      onClose={close}
      title={isNew ? 'Νέος υποψήφιος' : (lead?.name || '…')}
      subtitle={lead ? (
        <span className="inline-flex flex-wrap items-center gap-2">
          <StatusChip tone={statusTone(lead.status)}>{statusLabel(lead.status)}</StatusChip>
          <span>{[kindLabel(lead.kind), lead.area, sourceLabel(lead.source)].filter(Boolean).join(' · ')}</span>
        </span>
      ) : undefined}
      width="lg"
      testId="sales-lead-panel"
      footerStart={isSuper && lead ? (
        <button type="button" className={DANGER_GHOST_BTN} onClick={remove} disabled={busy}>
          <Trash2 className="w-4 h-4" aria-hidden="true" /> Διαγραφή
        </button>
      ) : undefined}
      footer={(
        <>
          <button type="button" className={GHOST_BTN} onClick={close}>Κλείσιμο</button>
          <button type="submit" form="sales-lead-form" className={PRIMARY_BTN} disabled={busy || loading || (!isNew && !form.dirty)}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
            {isNew ? 'Καταχώριση' : 'Αποθήκευση'}
          </button>
        </>
      )}
    >
      {loading ? (
        <div role="status" className="flex items-center gap-2 py-10 justify-center text-sm text-t3">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Φόρτωση…
        </div>
      ) : (
        <form id="sales-lead-form" className="space-y-6" onSubmit={e => { e.preventDefault(); save(); }}>
          {error && <Notice tone="danger" testId="sales-lead-error">{error}</Notice>}
          {duplicateNotice}

          {lead && (lead.phone || lead.email || lead.website) && (
            <div className="flex flex-wrap gap-2">
              {lead.phone && <a className={ASIDE_BTN} href={`tel:${lead.phone.replace(/\s/g, '')}`}><Phone className="w-4 h-4" aria-hidden="true" />{lead.phone}</a>}
              {lead.email && <a className={ASIDE_BTN} href={`mailto:${lead.email}`}><Mail className="w-4 h-4" aria-hidden="true" />{lead.email}</a>}
              {lead.website && <a className={ASIDE_BTN} href={lead.website} target="_blank" rel="noreferrer"><Globe className="w-4 h-4" aria-hidden="true" />Site</a>}
            </div>
          )}

          {isNew ? (
            <>
              {details}
              {nextStep}
            </>
          ) : (
            <>
              {briefing}
              {nextStep}
              {contactSection}
              {history}
              {details}
            </>
          )}
        </form>
      )}
    </DetailPanel>
  );
};

export default LeadPanel;
